import axios from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheck } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const demoOptions = [
  "All in One",
  "HRMS",
  "Project",
  "Payroll",
  "ATS",
  "EMS",
  "Performance",
  "Field Force",
];

export default function LookingforPayroll({
  heading,
  subHeading,
  description,
  features = [],
  buttonText = "Book Free Demo",
}) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched", // show error after focus + blur
  });
  const selectedEmployee = watch("employees");
  const selecteddemorequest = watch("demorequest");

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = {
        ...data,
        pageSource: document.title,
        pageUrl: window.location.href,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_APIBASE_URL}/payslips/contact-book-demo`,
        payload,
      );

      if (response.data.success) {
        // Swal.fire({
        //   icon: "success",
        //   title: "Demo Request Submitted",
        //   text: "Our team will contact you shortly.",
        //   confirmButtonColor: "#10324F",
        // });
        navigate("/thank-you-demo");
        reset();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: error || "Please try again later.",
        confirmButtonColor: "#10324F",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const employeeOptions = ["1-10", "11-25", "26-50", "51-100", "100+"];

  const inputClass =
    "w-full border-b py-3 outline-none transition-colors text-[#10324F] placeholder:text-[#52667d] focus:border-[#10324F]";

  return (
    <section className="py-10 px-6 bg-[#F6F9FB]">
      <div className="max-w-6xl mx-auto p-2 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-140">
          {/* Left Content */}
          <div>
            <h2 className="text-[#10324F] text-2xl font-bold mb-4">
              {subHeading}
            </h2>

            <p className="text-[#10324F] leading-8 mb-8">{description}</p>

            <h2 className="text-[#10324F] text-xl font-bold mb-6">
              Why Growing Businesses Prefer Waggex
            </h2>

            <ul className="space-y-4">
              {features.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 text-[#10324F]"
                >
                  <FaCheck className="text-sm shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <div
            className="border border-[#D1DFEA] bg-white rounded-xl p-5 sm:p-6"
            style={{
              boxShadow: `
                rgb(167 191 210 / 30%) -15px -15px 20px inset,
                rgb(170 188 202 / 20%) -20px -20px 30px
              `,
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Full Name */}
              <div>
                <input
                  type="text"
                  placeholder="Full Name"
                  {...register("fullName", {
                    required: "Full Name is required",
                  })}
                  className={`${inputClass} ${
                    errors.fullName
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.fullName.message}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <input
                  type="email"
                  placeholder="Work Email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+\.\S+$/,
                      message: "Enter a valid email address",
                    },
                  })}
                  className={`${inputClass} ${
                    errors.email
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <div
                  className={`flex items-center h-12 ${inputClass} ${
                    errors.phone
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                >
                  {/* Country Code */}
                  <div className="px-4 text-[#52667d] font-medium border-r border-gray-300 select-none">
                    +91
                  </div>

                  {/* Phone Input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Phone Number"
                    maxLength={10}
                    {...register("phone", {
                      required: "Phone Number is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Enter a valid 10-digit phone number",
                      },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    className="flex-1 h-full px-4 outline-none placeholder:text-[#52667d]"
                  />
                </div>

                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.phone.message}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <input
                  type="text"
                  placeholder="Company Name"
                  {...register("company", {
                    required: "Company Name is required",
                  })}
                  className={`${inputClass} ${
                    errors.company
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                />
                {errors.company && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.company.message}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <input
                  type="url"
                  placeholder="Website"
                  className={`${inputClass}
        ${
          errors.website
            ? "border-red-500"
            : "border-gray-300 focus:border-[#10324F]"
        }`}
                  {...register("website", {
                    required: "Website is required",
                    pattern: {
                      value: /^(https?:\/\/)?([\w\d-]+\.)+\w{2,}(\/.*)?$/,
                      message: "Enter valid website",
                    },
                  })}
                />
                {errors.website && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.website.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="employees" className="sr-only">
                  Employee
                </label>

                <select
                  id="employees"
                  className={`${inputClass}   ${selectedEmployee ? "text-[#10324F]" : "text-[#52667d]"}  focus:border-gray-500 ${
                    errors.employees
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                  {...register("employees", {
                    required: "Number of Employees is required",
                  })}
                >
                  <option value="" className="text-[#52667d]">
                    Select employees
                  </option>
                  {employeeOptions.map((item) => (
                    <option key={item} className="text-[#10324F]">
                      {item}
                    </option>
                  ))}
                </select>
                {errors.employees && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.employees.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="demorequest" className="sr-only">
                  Book demo
                </label>

                <select
                  id="demorequest"
                  className={`${inputClass}   ${selecteddemorequest ? "text-[#10324F]" : "text-[#52667d]"}  focus:border-gray-500 ${
                    errors.demorequest
                      ? "border-red-500"
                      : "border-gray-300 focus:border-[#10324F]"
                  }`}
                  {...register("demorequest", {
                    required: "Book demo is required",
                  })}
                >
                  <option value="" className="text-[#52667d]">
                    Book Demo
                  </option>
                  {demoOptions.map((item) => (
                    <option key={item} className="text-[#10324F]">
                      {item}
                    </option>
                  ))}
                </select>
                {errors.demorequest && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.demorequest.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#10324F] text-white py-3 rounded-xl hover:opacity-90 transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Submitting...
                  </>
                ) : (
                  buttonText
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
